import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useShowContext } from 'framework/show';
import ContactSection from '../shared/ContactSection';
import { EmployeePersonTile } from 'domain/shared/core/PersonTile';
import { generateLinkByEmail } from 'framework/utils';
import { READ_EMPLOYEE_PERMISSION, useEmployeeNamesService, useEmployeePermissionsMap } from 'domain/shared/employee';

const normalizeMenteeIds = (menteeIdsData: any[]) => {
  if (!Array.isArray(menteeIdsData)) return [];
  if (typeof menteeIdsData[0] === 'string') return menteeIdsData;
  return menteeIdsData?.map(item => item.alias) ?? [];
};

const MenteesSection = ({ menteesData }: { menteesData: any[] }) => {
  const aliases = menteesData?.map(m => m.alias) || [];
  const { permissionsMap } = useEmployeePermissionsMap(aliases);

  return (
    <Box>
      {menteesData?.map(mentee => (
        <Box key={mentee.alias} sx={{ mb: 2 }}>
          <EmployeePersonTile
            employeeId={mentee.alias}
            title="Mentee"
            size="medium"
            isLink={
              Array.isArray(permissionsMap[mentee.alias]) &&
              permissionsMap[mentee.alias].includes(READ_EMPLOYEE_PERMISSION)
            }
            showTeamsButton
            teamsIconSize="medium"
            teamsUrl={generateLinkByEmail(mentee.workingEmail)}
          />
        </Box>
      ))}
    </Box>
  );
};

const MentorshipSection: React.FC = () => {
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const { data: employee } = useShowContext();
  const [mentorData, setMentorData] = useState<any[]>([]);
  const [menteesData, setMenteesData] = useState<any[]>([]);

  const aliases = mentorData?.map(m => m.alias) || [];
  const { permissionsMap } = useEmployeePermissionsMap(aliases);

  const mentorId = employee?.getValueBySource('mentorshipInformation.mentor');
  const menteeIds = useMemo(
    () => normalizeMenteeIds(employee?.getValueBySource('mentorshipInformation.mentees')),
    [employee],
  );

  useEffect(() => {
    const fetchMentorshipData = async () => {
      if (mentorId) {
        const mentor = await getEmployeeNamesByAliases([mentorId]);
        setMentorData(mentor);
      }
      if (menteeIds.length > 0) {
        const mentees = await getEmployeeNamesByAliases(menteeIds);
        setMenteesData(mentees);
      }
    };

    fetchMentorshipData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentorId, menteeIds]);

  const description = (
    <Box>
      {['Provide feedback on your work', 'Define improvement areas'].map(text => (
        <Box key={text} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              aspectRatio: 1,
              bgcolor: 'success.main',
              mr: 1.5,
            }}
          />
          <Typography variant="body2">{text}</Typography>
        </Box>
      ))}
    </Box>
  );

  const content = (
    <Box>
      {mentorData.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <EmployeePersonTile
            employeeId={mentorData[0].alias}
            title="Mentor"
            size="medium"
            isLink={
              Array.isArray(permissionsMap[mentorId]) && permissionsMap[mentorId].includes(READ_EMPLOYEE_PERMISSION)
            }
            showTeamsButton
            teamsIconSize="medium"
            teamsUrl={generateLinkByEmail(mentorData[0].workingEmail)}
          />
        </Box>
      )}

      {menteesData.length > 0 && <MenteesSection menteesData={menteesData} />}
      {!menteesData.length && !mentorData.length && (
        <Box>
          <Typography variant="body1" color="textSecondary" fontStyle="italic" pt={1} px={2} pb={3}>
            No mentorship information available
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <ContactSection title="Mentorship" subtitle="Issues mentor can help with" description={description}>
      {content}
    </ContactSection>
  );
};

export default MentorshipSection;
